import { useState, useEffect, Fragment } from 'react';
import { useAuth } from '../context/AuthContext';
import { rbacAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Shield, Edit, RefreshCw, Loader2, Save, Users, Lock, LayoutGrid, Check, Minus
} from 'lucide-react';

const categoryColors = {
  core: 'bg-blue-100 text-blue-800',
  hr: 'bg-green-100 text-green-800',
  finance: 'bg-yellow-100 text-yellow-800',
  sales: 'bg-cyan-100 text-cyan-800',
  admin: 'bg-purple-100 text-purple-800',
  self_service: 'bg-gray-100 text-gray-800'
};

export default function RBACPage() {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [editRole, setEditRole] = useState(null);
  const [editModules, setEditModules] = useState([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showMatrix, setShowMatrix] = useState(false);

  const fetchData = async () => {
    try {
      const [rolesRes, modulesRes] = await Promise.all([
        rbacAPI.getRoles(),
        rbacAPI.getModules()
      ]);
      setRoles(rolesRes.data.roles || []);
      setModules(modulesRes.data.modules || []);
    } catch (error) {
      toast.error('Failed to load RBAC data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEditRole = (role) => {
    setEditRole(role);
    setEditModules([...role.modules]);
    setShowEditDialog(true);
  };

  const handleToggleModule = (moduleId) => {
    if (editModules.includes(moduleId)) {
      setEditModules(editModules.filter(m => m !== moduleId));
    } else {
      setEditModules([...editModules, moduleId]);
    }
  };

  const handleSavePermissions = async () => {
    setSaving(true);
    try {
      await rbacAPI.updateRolePermissions(editRole.role, {
        modules: editModules,
        description: editRole.description
      });
      toast.success(`Permissions updated for ${editRole.role}`);
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleResetRole = async (role) => {
    if (!confirm(`Reset ${role} to default permissions?`)) return;
    try {
      await rbacAPI.resetRolePermissions(role);
      toast.success(`${role} reset to default permissions`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset permissions');
    }
  };

  // Group modules by category
  const modulesByCategory = modules.reduce((acc, mod) => {
    const cat = mod.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(mod);
    return acc;
  }, {});

  // Matrix helpers
  const roleModuleSet = (role) => {
    if (role.modules.includes('*')) return new Set(modules.map((m) => m.id));
    return new Set(role.modules);
  };
  const countColor = (size) => {
    const ratio = modules.length ? size / modules.length : 0;
    if (ratio >= 0.7) return 'bg-red-100 text-red-800';
    if (ratio >= 0.4) return 'bg-amber-100 text-amber-800';
    return 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Role Permissions
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure module access for each role
          </p>
        </div>
        <Button variant="outline" onClick={() => setShowMatrix(true)} data-testid="compare-roles-btn">
          <LayoutGrid className="w-4 h-4 mr-2" /> Compare Roles
        </Button>
      </div>

      {/* Roles Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5" />
            Roles & Permissions
          </CardTitle>
          <CardDescription>
            Click edit to customize which modules each role can access
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Modules Access</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.role}>
                  <TableCell className="font-medium capitalize">
                    {role.role.replace('_', ' ')}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {role.description}
                  </TableCell>
                  <TableCell>
                    {role.modules.includes('*') ? (
                      <Badge className="bg-red-100 text-red-800">All Modules</Badge>
                    ) : (
                      <span>{role.modules.length} modules</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {role.is_custom ? (
                      <Badge variant="outline" className="text-orange-600 border-orange-600">
                        Customized
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-green-600 border-green-600">
                        Default
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {role.role !== 'super_admin' && (
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditRole(role)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        {role.is_custom && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResetRole(role.role)}
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Reset
                          </Button>
                        )}
                      </div>
                    )}
                    {role.role === 'super_admin' && (
                      <span className="text-muted-foreground text-sm flex items-center justify-end gap-1">
                        <Lock className="w-3 h-3" /> Protected
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="capitalize">
              Edit {editRole?.role?.replace('_', ' ')} Permissions
            </DialogTitle>
            <DialogDescription>
              Select which modules this role can access
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {Object.entries(modulesByCategory).map(([category, mods]) => (
              <div key={category}>
                <h4 className="font-medium mb-3 capitalize flex items-center gap-2">
                  <Badge className={categoryColors[category] || 'bg-gray-100'}>
                    {category.replace('_', ' ')}
                  </Badge>
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {mods.map((mod) => (
                    <label
                      key={mod.id}
                      className="flex items-center gap-2 p-2 rounded-lg border cursor-pointer hover:bg-muted/50"
                    >
                      <Checkbox
                        checked={editModules.includes(mod.id)}
                        onCheckedChange={() => handleToggleModule(mod.id)}
                      />
                      <span className="text-sm">{mod.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePermissions} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Permissions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Compare Matrix Dialog */}
      <Dialog open={showMatrix} onOpenChange={setShowMatrix}>
        <DialogContent className="max-w-6xl max-h-[88vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LayoutGrid className="w-5 h-5" /> Role Access Matrix
            </DialogTitle>
            <DialogDescription>
              Every screen (row) across every role (column). Redder header counts mean broader access — watch for over-permissioned roles.
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-auto border rounded-lg">
            <table className="w-full text-sm border-collapse" data-testid="role-matrix">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-background text-left p-2 border-b min-w-[180px]">Screen</th>
                  {roles.map((r) => (
                    <th key={r.role} className="p-2 border-b text-center whitespace-nowrap">
                      <div className="capitalize font-medium">{r.role.replace('_', ' ')}</div>
                      <Badge className={countColor(roleModuleSet(r).size)}>
                        {r.modules.includes('*') ? 'All' : roleModuleSet(r).size}
                      </Badge>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(modulesByCategory).map(([category, mods]) => (
                  <Fragment key={category}>
                    <tr>
                      <td colSpan={roles.length + 1} className="bg-muted/50 p-1.5 font-medium capitalize text-xs">
                        {category.replace('_', ' ')}
                      </td>
                    </tr>
                    {mods.map((mod) => (
                      <tr key={mod.id} className="hover:bg-muted/30">
                        <td className="sticky left-0 z-10 bg-background p-2 border-b">{mod.name}</td>
                        {roles.map((r) => (
                          <td key={r.role} className="p-2 border-b text-center">
                            {roleModuleSet(r).has(mod.id) ? (
                              <Check className="w-4 h-4 text-green-600 inline" />
                            ) : (
                              <Minus className="w-3 h-3 text-muted-foreground/40 inline" />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMatrix(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
